import { useState, useEffect } from 'react';
import { BookOpen, Search, ThumbsUp, Eye, Clock, ChevronRight, Plus, Loader2, Save, X, Tag, FolderOpen, ChevronDown } from 'lucide-react';
import { kbAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function KnowledgeBasePage() {
  const { user } = useAuth();
  const isEndUser = (user?.role_name || '').toLowerCase().replace(/\s+/g, '_') === 'end_user';
  const [ticketCategories, setTicketCategories] = useState([]); // from Admin > Categories
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: '', tags: '', status: 'published', is_featured: false });

  const fetch = async () => {
    setLoading(true);
    try {
      const [cRes, aRes] = await Promise.all([
        kbAPI.ticketCategories(),                                     // Admin ticket categories
        kbAPI.listArticles({ search, category: selectedCategory || undefined }),
      ]);
      setTicketCategories(cRes.data || []);
      setArticles(aRes.data?.results || aRes.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); // eslint-disable-next-line
  }, [selectedCategory]);

  const handleSearch = (e) => { e.preventDefault(); fetch(); };

  const handleSave = async () => {
    if (!form.title || !form.content) { toast.error('Title and content required'); return; }
    if (saving) return;
    setSaving(true);
    try {
      const data = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()) : [] };
      await kbAPI.createArticle(data);
      toast.success('Article created');
      setShowForm(false);
      setForm({ title: '', content: '', category: '', tags: '', status: 'published', is_featured: false });
      fetch();
    } catch (err) { toast.error(JSON.stringify(err.response?.data) || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleVote = async (id, vote) => {
    try {
      await kbAPI.voteHelpful(id, vote);
      toast.success('Thanks for your feedback!');
    } catch {}
  };

  const openArticle = async (article) => {
    setSelectedArticle(article);
    try { await kbAPI.recordView(article.id); } catch {}
  };

  // Article detail view
  if (selectedArticle) {
    return (
      <div data-testid="kb-article-view">
        <button onClick={() => setSelectedArticle(null)} className="text-sm text-sky-600 hover:text-sky-800 mb-4 flex items-center gap-1" data-testid="back-to-kb">
          <ChevronRight className="w-4 h-4 rotate-180" /> Back to Knowledge Base
        </button>
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-400 px-2 py-0.5 rounded">{selectedArticle.category_name}</span>
            {selectedArticle.tags?.map(t => <span key={t} className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded">{t}</span>)}
          </div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-4">{selectedArticle.title}</h1>
          <div className="flex items-center gap-4 text-xs text-slate-500 mb-6">
            <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {selectedArticle.views} views</span>
            <span className="flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" /> {selectedArticle.helpful_percent}% helpful</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> by {selectedArticle.author_name}</span>
          </div>
          <div className="prose prose-sm max-w-none text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{selectedArticle.content}</div>
          <div className="mt-8 pt-6 border-t dark:border-slate-700 flex items-center justify-between">
            <span className="text-sm text-slate-600 dark:text-slate-400">Was this article helpful?</span>
            <div className="flex gap-2">
              <button onClick={() => handleVote(selectedArticle.id, 'yes')} className="bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg text-sm hover:bg-emerald-100 flex items-center gap-1.5"><ThumbsUp className="w-4 h-4" /> Yes</button>
              <button onClick={() => handleVote(selectedArticle.id, 'no')} className="bg-slate-100 text-slate-600 px-4 py-2 rounded-lg text-sm hover:bg-slate-200 flex items-center gap-1.5"><ThumbsUp className="w-4 h-4 rotate-180" /> No</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="knowledge-base-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-sky-500" /> Knowledge Base
          </h2>
          <p className="text-sm text-slate-500 mt-1">Find answers, guides, and troubleshooting articles</p>
        </div>
        {!isEndUser && (
        <button onClick={() => setShowForm(true)} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600" data-testid="new-article-btn">
          <Plus className="w-4 h-4" /> New Article
        </button>
        )}
      </div>

      {/* New Article Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6 mb-6">
          <h3 className="font-semibold mb-4 dark:text-white">New Article</h3>
          <div className="space-y-4">
            <input placeholder="Article Title *" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full border dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:bg-slate-900 dark:text-white" />
            <div className="grid grid-cols-3 gap-3">
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
                <option value="">Select Category</option>
                {ticketCategories.map(parent => (
                  <optgroup key={parent.id} label={parent.name}>
                    <option value={parent.id}>{parent.name}</option>
                    {parent.subcategories?.map(sub => (
                      <option key={sub.id} value={sub.id}>{'  ↳ ' + sub.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <input placeholder="Tags (comma-separated)" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
                <option value="published">Published</option>
                <option value="draft">Draft (needs review)</option>
              </select>
            </div>
            <textarea rows={8} placeholder="Article content... (supports plain text)" value={form.content} onChange={e => setForm({...form, content: e.target.value})} className="w-full border dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:bg-slate-900 dark:text-white" />
            <div className="flex gap-2">
              <button onClick={handleSave} disabled={saving} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-1.5 hover:bg-sky-600 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Publish</button>
              <button onClick={() => setShowForm(false)} className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2 rounded-xl text-sm"><X className="w-4 h-4 inline mr-1" />Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <form onSubmit={handleSearch} className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles..."
          className="w-full pl-12 pr-4 py-3.5 border dark:border-slate-700 rounded-xl text-sm dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-sky-500 outline-none" data-testid="kb-search" />
      </form>

      {/* Categories — Admin ticket categories with subcategories */}
      <div className="flex gap-2 flex-wrap mb-6">
        <button onClick={() => setSelectedCategory(null)}
          className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${!selectedCategory ? 'bg-sky-500 text-white border-sky-500' : 'bg-white dark:bg-slate-800 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
          All Articles
        </button>
        {ticketCategories.map(cat => (
          <div key={cat.id} className="flex items-center gap-1 flex-wrap">
            <button onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${selectedCategory === cat.id ? 'bg-sky-500 text-white border-sky-500' : 'bg-white dark:bg-slate-800 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
              <FolderOpen className="w-3 h-3" /> {cat.name}
            </button>
            {cat.subcategories?.map(sub => (
              <button key={sub.id} onClick={() => setSelectedCategory(selectedCategory === sub.id ? null : sub.id)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs border transition-all ${selectedCategory === sub.id ? 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-400 border-sky-300' : 'bg-slate-50 dark:bg-slate-800/60 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-700'}`}>
                <ChevronRight className="w-2.5 h-2.5" /> {sub.name}
              </button>
            ))}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm divide-y dark:divide-slate-700">
          {articles.length === 0 ? (
            <div className="p-8 text-center text-slate-400">No articles found</div>
          ) : articles.map(article => (
            <div key={article.id} onClick={() => openArticle(article)} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-all flex items-center justify-between" data-testid={`article-${article.id}`}>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {article.is_featured && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Featured</span>}
                  <h4 className="font-medium text-slate-800 dark:text-white text-sm">{article.title}</h4>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded flex items-center gap-1">
                    {article.category_parent_name && <><span className="text-slate-400">{article.category_parent_name}</span><ChevronRight className="w-3 h-3 text-slate-300" /></>}
                    {article.category_name}
                  </span>
                  <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" /> {article.views}</span>
                  <span className="flex items-center gap-0.5"><ThumbsUp className="w-3 h-3" /> {article.helpful_percent}%</span>
                  {article.tags?.map(t => <span key={t} className="flex items-center gap-0.5"><Tag className="w-3 h-3" /> {t}</span>)}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
